import React from 'react';
import { matchPath } from 'react-router';
// eslint-disable-next-line no-restricted-imports
import { DefaultButton, PrimaryButton, Stack, useTheme } from '@fluentui/react';
import clsx from 'clsx';
import { IAppTheme, pageDataService } from 'common';
import { useNavigation } from 'common/hooks/useNavigation';
import TelemetryProvider from 'common/utilities/TelemetryProvider';
import { useLocalizedStrings } from 'i18n';
import { routes } from 'Routes';

import { getStyles, tokens } from './BaseButton.styles';
import { IBaseButtonProps } from './BaseButton.types';

export const BaseButton = (props: IBaseButtonProps) => {
  const {
    isLoading,
    loadingAriaLabel,
    href,
    componentAs: ComponentAs = DefaultButton,
    onClick,
    onRenderIcon,
    styles,
    telemetryEventName,
    telemetryProps,
    telemetryEventGroup,
    useWebViewNavigation = true,
    ...otherProps
  } = props;

  const { t } = useLocalizedStrings();
  const theme = useTheme() as IAppTheme;
  const classNames = getStyles(theme, styles);
  const { navigateTo } = useNavigation();
  const isWebView = pageDataService.isWebView();
  const useFastNavigate = React.useMemo(() => (href ? routes.some((route) => matchPath(href, route)) : false), [href]);

  const onBtnClick = React.useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      if (onClick || href) {
        if (telemetryEventName) {
          if (typeof telemetryEventName === 'string') {
            TelemetryProvider.Instance.logUserActivity(telemetryEventName, telemetryEventGroup || 'ClickActivity', telemetryProps);
          } else {
            telemetryEventName.forEach((eventName) => {
              TelemetryProvider.Instance.logUserActivity(eventName, telemetryEventGroup || 'ClickActivity', telemetryProps);
            });
          }
        }

        if (onClick) {
          onClick(e);
        }

        // if the button is a link to a Gibralatar route and if event default was not prevented from onClick handler
        // then do a fast switch.
        if (href && useFastNavigate && !e.ctrlKey && !e.metaKey && !e.shiftKey && !e.defaultPrevented) {
          navigateTo(href, useWebViewNavigation && isWebView);
          e.preventDefault();
        }
      }
    },
    [
      onClick,
      href,
      telemetryEventName,
      useFastNavigate,
      telemetryEventGroup,
      telemetryProps,
      navigateTo,
      useWebViewNavigation,
      isWebView,
    ]
  );

  const onRenderBtnIcon = React.useCallback(() => {
    if (onRenderIcon) {
      return <span className={classNames.icon}>{onRenderIcon()}</span>;
    }
    return null;
  }, [classNames.icon, onRenderIcon]);

  if (isLoading) {
    const dot = (
      <span className={clsx(classNames.dot, ComponentAs === PrimaryButton ? classNames.primary : classNames.ghost)} aria-hidden />
    );
    return (
      <ComponentAs {...otherProps} aria-label={loadingAriaLabel || t('Loading')} text={undefined} menuProps={undefined}>
        <Stack tokens={tokens} disableShrink horizontal>
          {dot}
          {dot}
          {dot}
        </Stack>
      </ComponentAs>
    );
  }
  return (
    <ComponentAs
      href={href}
      onClick={onBtnClick}
      onRenderIcon={onRenderIcon ? onRenderBtnIcon : undefined}
      styles={styles}
      {...otherProps}
    />
  );
};
